import Image from 'next/image'
import clsx from 'clsx'

import Link from '@/atoms/Link/Link'
import { SampleDesignCard } from '@/molecules/BlogCard/SampleCard'

/* utils */
import { INDUSTRY_CATEGORY_DATABASE_ID } from '@/utils/constants/revalidation'
import { getContentCategoryMetadata } from '@/utils/functions/samples'

/* styling */
import styles from './_.module.css'
import masonryStyles from '../../../styles/designMasonry.module.css'

interface Props {
  post: SamplePostDetails
  similarPosts: SamplePost[]
  contentCategory: WpContentCategoryName
}

function DesignSampleDetails({
  post,
  similarPosts,
  contentCategory
}: Props): JSX.Element {
  const contentCategoryMetadata = getContentCategoryMetadata(contentCategory)

  const industry = post.categories.edges.find(
    category => category.node.parentDatabaseId === INDUSTRY_CATEGORY_DATABASE_ID
  )

  const creatorLevel = post.categories.edges.find(category =>
    category.node.name.startsWith('Creator Level: ')
  )

  const postMetadata = [
    {
      icon: '/assets/icons/bag.svg',
      label: 'Industry',
      value: industry?.node.name ?? ''
    },
    {
      icon: '/assets/icons/pen-tool-dark.svg',
      label: 'Creator Level',
      value: creatorLevel?.node.name.split('Creator Level: ')[1] ?? ''
    },
    {
      icon: '/assets/icons/folder-vector.svg',
      label: 'Purpose',
      value: post.sampleDesignPostMetadata?.purpose
    }
  ]

  // type narrowing for design posts
  if (!post.sampleDesignPostMetadata) return <></>

  return (
    <div>
      <article className="max-desktop-width  |  grid gap-8 md:grid-cols-12  |  mx-auto  mb-16 px-5 sm:px-10 lg:px-6">
        {/* space col */}
        <div className="hidden md:block  |  md:col-span-1 md:order-2" />

        {/* sample metadata */}
        <section className="md:col-span-4 md:order-3">
          <h2 className="h4 font-medium  |  mb-8">{post.title}</h2>
          <div
            className={clsx(styles.blogContent, 'mb-8')}
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
          {post.sampleDesignPostMetadata.creatorProfile && (
            <div className="text-paragraph-3  |  mb-8">
              <h2 className="font-medium  |  mb-1">Creator Profile</h2>
              <div
                className="text-gray-70"
                dangerouslySetInnerHTML={{
                  __html: post.sampleDesignPostMetadata.creatorProfile
                }}
              />
            </div>
          )}
          <div className="grid gap-6">
            {postMetadata.map(metadata => {
              if (metadata.value)
                return (
                  <div key={metadata.value} className="flex items-center">
                    <img
                      src={metadata.icon}
                      alt={metadata.label}
                      className="mr-6"
                    />
                    <div>
                      <span className="block  |  text-gray-50  text-caption tracking-wider uppercase font-medium">
                        {metadata.label}
                      </span>
                      <span className="text-paragraph-3 font-medium">
                        {metadata.value}
                      </span>
                    </div>
                  </div>
                )
            })}
          </div>
        </section>

        {/* space col */}
        <div className="hidden md:block  |  md:col-span-1 md:order-4" />

        {/* sample image */}
        <section className="md:col-span-6 md:order-1">
          <Image
            alt={post.title}
            src={post.sampleDesignPostMetadata.image.sourceUrl}
            width={post.sampleDesignPostMetadata.image.mediaDetails.width}
            height={post.sampleDesignPostMetadata.image.mediaDetails.height}
            className="w-full"
          />
        </section>
      </article>

      <section className="max-desktop-width px-5 sm:px-10 lg:px-6 mx-auto |   border-t border-gray-90  | py-16">
        <h3 className="h4 font-medium text-center  |  mb-10">
          More in {contentCategoryMetadata.title}
        </h3>
        <div className={clsx('mb-10', masonryStyles.designMasonryGrid)}>
          {similarPosts
            .filter(_post => _post.slug !== post.slug)
            .splice(0, 8)
            .map(_post => {
              if (!_post.sampleDesignPostMetadata) return
              return (
                <article key={_post.id}>
                  <SampleDesignCard
                    title={_post.title}
                    slug={`/samples/${_post.slug}/`}
                    categories={_post.categories.edges}
                    imageLink={_post.sampleDesignPostMetadata.image.sourceUrl}
                    imageWidth={
                      _post.sampleDesignPostMetadata.image.mediaDetails.width
                    }
                    imageHeight={
                      _post.sampleDesignPostMetadata.image.mediaDetails.height
                    }
                  />
                </article>
              )
            })}
        </div>

        <div className="flex items-center justify-center">
          <Link
            href={contentCategoryMetadata.slug}
            className="flex items-center justify-center  |  text-paragraph-4 font-medium bg-blue-50 text-white  |  rounded-full  |  h-8  |  px-6"
          >
            View All Samples
          </Link>
        </div>
      </section>
    </div>
  )
}

export default DesignSampleDetails
